import { Route, Routes, useLocation } from 'react-router-dom';
import AuthRoute from '../components/route/AuthRoute';
import { UserCompaniesPage } from './Admin/Company/UserCompaniesPage';
import { UserCompaniesCreatePage } from './Admin/Company/UserCompaniesCreatePage';
import { UserCompaniesUpdatePage } from './Admin/Company/UserCompaniesUpdatePage';
import { DashboardPage } from './Admin/DashboardPage/DashboardPage';
import { ForgotPasswordPage } from './Admin/ForgotPassword/ForgotPasswordPage';
import { WelcomePage } from './Admin/HomePage/HomePage';
import { InvoiceCreatePage } from './Admin/InvoicePage/InvoiceCreatePage';
import { RegisterPage } from './Admin/Register/RegisterPage';
import { ResetPasswordPage } from './Admin/ResetPassword/ResetPasswordPage';
import { SignInPage } from './Admin/SignIn/SignInPage';
import { UserTeamsCreatePage } from './Admin/Team/UserView/UserTeamsCreatePage';
import { UserTeamsUpdatePage } from './Admin/Team/UserView/UserTeamsUpdatePage';
import { UserTeamsPage } from './Admin/Team/UserView/UserTeamsPage';
import { AdminApp } from './AdminApp';
import { InvoicePage } from './Admin/InvoicePage/InvoicePage';
import { InvoiceUpdatePage } from './Admin/InvoicePage/InvoiceUpdatePage';
import { CompanyBankAccountsCreatePage } from './Admin/CompanyBankAccount/CompanyBankAccountsCreatePage';
import { CompanyBankAccountsUpdatePage } from './Admin/CompanyBankAccount/CompanyBankAccountsUpdatePage';
import AdminLayout from '../components/layout/Client/AdminLayout';
import { NumericalSeriesCreatePage } from './Admin/NumericalSeries/NumericalSeriesCreatePage';
import { NumericalSeriesUpdatePage } from './Admin/NumericalSeries/NumericalSeriesUpdatePage';
import AuthProvider from '../context/AuthContext';
import CompanyProvider from '../hooks/useCompany';
import { PrintInvoicePage } from './PrintInvoice/PrintInvoicePage';
import { FilesPage } from './Admin/Files/FilesPage';
import { LoadingProvider } from '../context/loadingContext';
import { UserTeamsMemberPage } from './Admin/Team/UserView/UserTeamsMemberPage';
import { CurrencyRatesCreatePage } from './Admin/CurrencyRates/CurrencyRatesCreatePage';
import { CurrencyRatesUpdatePage } from './Admin/CurrencyRates/CurrencyRatesUpdatePage';
import { NumericalSeriesCounterUpdatePage } from './Admin/NumericalSeriesCounter/NumericalSeriesCounterUpdatePage';
import { CodeBookItemsPage } from './Admin/CodeBookItem/CodeBookItemPage';
import { CodeBookItemsCreatePage } from './Admin/CodeBookItem/CodeBookItemCreatePage';
import { CodeBookItemsUpdatePage } from './Admin/CodeBookItem/CodeBookItemUpdatePage';
import { UserProfileUpdatePage } from './Admin/UserProfile/UserProfileUpdatePage';
import { CodeBookItemsPriceUpdatePage } from './Admin/CodeBookItemPrice/CodeBookItemPriceUpdatePage';
import { CodeBookItemsPriceCreatePage } from './Admin/CodeBookItemPrice/CodeBookItemPriceCreatePage';
import { UserTaskPage } from './Admin/UserTask/UserTaskPage';
import { UserTaskCreatePage } from './Admin/UserTask/UserTaskCreatePage';
import { UserTaskUpdatePage } from './Admin/UserTask/UserTaskUpdatePage';
import { BreadcrumbsProvider } from '../hooks/useBreadcrumbs';
import { DocumentOptionalFieldsCreatePage } from './Admin/DocumentOptionalFields/DocumentOptionalFieldsCreatePage';
import { DocumentOptionalFieldsUpdatePage } from './Admin/DocumentOptionalFields/DocumentOptionalFieldsUpdatePage';
import { SmartAttachmentCreatePage } from './Admin/SmartAttachment/SmartAttachmentCreatePage';
import { SmartAttachmentUpdatePage } from './Admin/SmartAttachment/SmartAttachmentUpdatePage';
import { SmartAttachmentPrintPage } from './Admin/SmartAttachment/SmartAttachmentPrintPage';
import { CompanyTeamsUpdatePage } from './Admin/Team/UserView/CompanyTeamsUpdatePage';
import { IssuedDocumentsExport } from './Admin/IssuedDocumentsExport/IssuedDocumentsExport';

export const paths = {
  SIGNIN: 'signin',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  REGISTER: 'register',
  HOME: '/',
  DASHBOARD: 'dashboard',
  NEW_INVOICE: 'new-document',
  INVOICE: 'document',
  INVOICES: 'documents',
  COMPANY_TEAMS: 'company-teams',
  MEMBERS: 'members',
  COMPANY_BANK_ACCOUNTS: 'company-bank-accounts',
  CODE_BOOK_ITEMS: 'code-book-items',
  CODE_BOOK_ITEMS_PRICE: 'price',
  NUMERICAL_SERIES: 'numerical-series',
  NUMERICAL_SERIES_COUNTERS: 'counters',
  COMPANY_CURRENCY_RATES: 'currency-rates',
  FILES: 'files',
  MY_COMPANIES: 'my-companies',
  MY_TEAMS: 'my-teams',
  USER_SETTING: 'user-setting',
  USER_TASKS: 'user-tasks',
  OPTIONAL_FIELDS: 'optional-fields',
  SMART_ATTACHMENTS: 'smart-attachments',
  INVOICE_PDF_VIEW: 'invoice-pdf',
  SMART_ATTACHMENT_PDF_VIEW: 'smart-attachment-pdf',
  PDF_VIEW: 'preview-pdf',
  ISSUED_DOCUMENT_EXPORT: 'issued-documents-export',
};

export const RouterContainer = () => {
  const location = useLocation();
  const pathnameArr = location.pathname.split('/');

  // PDF generating needs separed pages
  if (pathnameArr[1] === paths.INVOICE_PDF_VIEW)
    return (
      <AuthProvider>
        <CompanyProvider>
          <PrintInvoicePage id={pathnameArr[2]} />
        </CompanyProvider>
      </AuthProvider>
    );
  if (pathnameArr[1] === paths.SMART_ATTACHMENT_PDF_VIEW)
    return (
      <AuthProvider>
        <SmartAttachmentPrintPage id={pathnameArr[2]} />
      </AuthProvider>
    );

  return (
    <AuthProvider>
      <CompanyProvider>
        <BreadcrumbsProvider>
          <AdminLayout>
            <LoadingProvider>
              <Routes>
                <Route path={paths.SIGNIN} element={<SignInPage />} />
                <Route path={paths.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
                <Route path={paths.RESET_PASSWORD} element={<ResetPasswordPage />} />
                <Route path={paths.REGISTER} element={<RegisterPage />} />
                <Route path={paths.HOME} element={<AdminApp />}>
                  <Route index element={<WelcomePage />} />
                  <Route
                    path={paths.DASHBOARD}
                    element={<AuthRoute component={<DashboardPage />} />}
                  />
                  <Route
                    path={paths.NEW_INVOICE}
                    element={<AuthRoute component={<InvoiceCreatePage />} />}
                  />
                  <Route
                    path={`${paths.INVOICE}/:id`}
                    element={<AuthRoute component={<InvoiceUpdatePage />} />}
                  />
                  <Route
                    path={paths.INVOICES}
                    element={<AuthRoute component={<InvoicePage />} />}
                  />
                  {/* Smart attachments */}
                  <Route
                    path={`${paths.INVOICE}/:documentId/${paths.SMART_ATTACHMENTS}/create`}
                    element={<AuthRoute component={<SmartAttachmentCreatePage />} />}
                  />
                  <Route
                    path={`${paths.INVOICE}/:documentId/${paths.SMART_ATTACHMENTS}/edit/:id`}
                    element={<AuthRoute component={<SmartAttachmentUpdatePage />} />}
                  />
                  {/* Company teams */}
                  <Route
                    path={`${paths.COMPANY_TEAMS}/:companyId/create`}
                    element={<AuthRoute component={<UserTeamsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.COMPANY_TEAMS}/:companyId/edit/:id`}
                    element={<AuthRoute component={<CompanyTeamsUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.COMPANY_TEAMS}/current/edit/:id`}
                    element={<AuthRoute component={<CompanyTeamsUpdatePage />} />}
                  />
                  {/* Company teams members */}
                  <Route
                    path={`${paths.COMPANY_TEAMS}/:companyId/edit/:teamId/${paths.MEMBERS}/edit/:membershipId`}
                    element={<AuthRoute component={<UserTeamsMemberPage />} />}
                  />
                  <Route
                    path={`${paths.COMPANY_TEAMS}/current/edit/:teamId/${paths.MEMBERS}/edit/:membershipId`}
                    element={<AuthRoute component={<UserTeamsMemberPage />} />}
                  />
                  {/* Company bank accounts */}
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.COMPANY_BANK_ACCOUNTS}/create`}
                    element={<AuthRoute component={<CompanyBankAccountsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.COMPANY_BANK_ACCOUNTS}/edit/:id`}
                    element={<AuthRoute component={<CompanyBankAccountsUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.COMPANY_BANK_ACCOUNTS}/create`}
                    element={<AuthRoute component={<CompanyBankAccountsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.COMPANY_BANK_ACCOUNTS}/edit/:id`}
                    element={<AuthRoute component={<CompanyBankAccountsUpdatePage />} />}
                  />
                  {/* Company optional fields */}
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.OPTIONAL_FIELDS}/create`}
                    element={<AuthRoute component={<DocumentOptionalFieldsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.OPTIONAL_FIELDS}/edit/:id`}
                    element={<AuthRoute component={<DocumentOptionalFieldsUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.OPTIONAL_FIELDS}/create`}
                    element={<AuthRoute component={<DocumentOptionalFieldsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.OPTIONAL_FIELDS}/edit/:id`}
                    element={<AuthRoute component={<DocumentOptionalFieldsUpdatePage />} />}
                  />
                  {/* Code book items */}
                  <Route
                    path={paths.CODE_BOOK_ITEMS}
                    element={<AuthRoute component={<CodeBookItemsPage />} />}
                  />
                  <Route
                    path={`${paths.CODE_BOOK_ITEMS}/create`}
                    element={<AuthRoute component={<CodeBookItemsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.CODE_BOOK_ITEMS}/edit/:id`}
                    element={<AuthRoute component={<CodeBookItemsUpdatePage />} />}
                  />
                  {/* Code book item prices */}
                  <Route
                    path={`${paths.CODE_BOOK_ITEMS}/:itemId/${paths.CODE_BOOK_ITEMS_PRICE}/create`}
                    element={<AuthRoute component={<CodeBookItemsPriceCreatePage />} />}
                  />
                  <Route
                    path={`${paths.CODE_BOOK_ITEMS}/:itemId/${paths.CODE_BOOK_ITEMS_PRICE}/edit/:id`}
                    element={<AuthRoute component={<CodeBookItemsPriceUpdatePage />} />}
                  />
                  {/* Files */}
                  <Route path={paths.FILES} element={<AuthRoute component={<FilesPage />} />} />
                  {/* Numerical series */}
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.NUMERICAL_SERIES}/create`}
                    element={<AuthRoute component={<NumericalSeriesCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.NUMERICAL_SERIES}/edit/:id`}
                    element={<AuthRoute component={<NumericalSeriesUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.NUMERICAL_SERIES}/create`}
                    element={<AuthRoute component={<NumericalSeriesCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.NUMERICAL_SERIES}/edit/:id`}
                    element={<AuthRoute component={<NumericalSeriesUpdatePage />} />}
                  />
                  {/* Numerical series counter */}
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.NUMERICAL_SERIES}/:numericalSeriesId/${paths.NUMERICAL_SERIES_COUNTERS}/edit/:id`}
                    element={<AuthRoute component={<NumericalSeriesCounterUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.NUMERICAL_SERIES}/:numericalSeriesId/${paths.NUMERICAL_SERIES_COUNTERS}/edit/:id`}
                    element={<AuthRoute component={<NumericalSeriesCounterUpdatePage />} />}
                  />
                  {/* Currency rates */}
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.COMPANY_CURRENCY_RATES}/create`}
                    element={<AuthRoute component={<CurrencyRatesCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/:companyId/${paths.COMPANY_CURRENCY_RATES}/edit/:id`}
                    element={<AuthRoute component={<CurrencyRatesUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.COMPANY_CURRENCY_RATES}/create`}
                    element={<AuthRoute component={<CurrencyRatesCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/current/${paths.COMPANY_CURRENCY_RATES}/edit/:id`}
                    element={<AuthRoute component={<CurrencyRatesUpdatePage />} />}
                  />
                  {/* User tasks */}
                  <Route
                    path={paths.USER_TASKS}
                    element={<AuthRoute component={<UserTaskPage />} />}
                  />
                  <Route
                    path={`${paths.USER_TASKS}/create`}
                    element={<AuthRoute component={<UserTaskCreatePage />} />}
                  />
                  <Route
                    path={`${paths.USER_TASKS}/edit/:id`}
                    element={<AuthRoute component={<UserTaskUpdatePage />} />}
                  />
                  {/* User companies */}
                  <Route
                    path={paths.MY_COMPANIES}
                    element={<AuthRoute component={<UserCompaniesPage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/create`}
                    element={<AuthRoute component={<UserCompaniesCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/edit/:id`}
                    element={<AuthRoute component={<UserCompaniesUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_COMPANIES}/edit/current`}
                    element={<AuthRoute component={<UserCompaniesUpdatePage />} />}
                  />
                  {/* User teams */}
                  <Route
                    path={paths.MY_TEAMS}
                    element={<AuthRoute component={<UserTeamsPage />} />}
                  />
                  <Route
                    path={`${paths.MY_TEAMS}/create`}
                    element={<AuthRoute component={<UserTeamsCreatePage />} />}
                  />
                  <Route
                    path={`${paths.MY_TEAMS}/edit/:id`}
                    element={<AuthRoute component={<UserTeamsUpdatePage />} />}
                  />
                  <Route
                    path={`${paths.ISSUED_DOCUMENT_EXPORT}`}
                    element={<AuthRoute component={<IssuedDocumentsExport />} />}
                  />
                  {/* User setting */}
                  <Route
                    path={`${paths.USER_SETTING}`}
                    element={<AuthRoute component={<UserProfileUpdatePage />} />}
                  />
                </Route>
              </Routes>
            </LoadingProvider>
          </AdminLayout>
        </BreadcrumbsProvider>
      </CompanyProvider>
    </AuthProvider>
  );
};
